import { useEffect, useState } from "react";
import { Container, Row, Col, Button } from "reactstrap";
import SearchBar from "./SearchBar/SearchBar";
import JobList from "./JobList/JobList";
import JobDetailsCard from "./JobDetails/JobDetailsCard";
import JobDetailsOffcanvas from "./JobDetails/JobDetailsOffcanvas";
import FilterBubble from "./SearchBar/FilterBubble";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchJobPortalJobList,
  selectJobPortalJob,
  setSearchValues,
  setPageNumber,
  resetJobPortalJobList,
  fetchJobPortalWorkLocations,
} from "../../store/job/action";
import "./HomeContent.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { searchDefault } from "./HomeContentConstants";

export default function HomeContent() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const jobId = urlParams.get("jobId");

  const [canvasOpen, setCanvasOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Get global state variables from Redux store
  const jobData = useSelector((state) => state.JobPortalJobReducer.jobData);
  const jobListRaw = useSelector((state) => state.JobPortalJobReducer.jobList);

  const selectedJob = useSelector(
    (state) => state.JobPortalJobReducer.selectedJob
  );
  const search = useSelector((state) => state?.JobPortalJobReducer.search);
  const pageNumber = useSelector(
    (state) => state.JobPortalJobReducer.pageNumber
  );
  const divScrollYPosition = useSelector(
    (state) => state.JobPortalJobReducer.divScrollYPosition
  );
  const windowScrollYPosition = useSelector(
    (state) => state.JobPortalJobReducer.windowScrollYPosition
  );

  // Handle window resize
  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch job list from the server if jobList is empty or if pageNumber exceeds jobData.number
  useEffect(() => {
    if ((jobListRaw.length === 0 || pageNumber > jobData.number) && !jobId) {
      dispatch(fetchJobPortalJobList(pageNumber));
    }
  }, [pageNumber]);

  // Select first job
  useEffect(() => {
    const includesObject = jobListRaw.some((job) => job.id === selectedJob?.id);
    if (windowWidth >= 1000 && !includesObject) {
      dispatch(selectJobPortalJob(jobListRaw[0]));
    }
  }, [windowWidth, jobListRaw]);

  // Scroll to the last scroll position when component is mounted
  useEffect(() => {
    if (windowWidth >= 1000) {
      const divElement = document.querySelector("#scrollableDiv");
      if (divElement && divScrollYPosition && windowScrollYPosition) {
        divElement.scrollTop = divScrollYPosition;
        window.scrollTo(0, windowScrollYPosition);
      }
    } else {
      window.scrollTo(0, windowScrollYPosition);
    }
  }, []);

  // Handle job selected and open offcanvas if windowWidth is <1000px
  function handleJobSelect(job) {
    dispatch(selectJobPortalJob(job));
    setCanvasOpen(!canvasOpen);
  }

  // Handle offcanvas close
  function handleCanvasClose() {
    setCanvasOpen(!canvasOpen);
  }

  function returnToJob() {
    dispatch(setSearchValues("keyword", ""));
    dispatch(setPageNumber(0))
      .then(() => dispatch(resetJobPortalJobList()))
      .then(() => dispatch(fetchJobPortalJobList(0)))
      .then(() => dispatch(fetchJobPortalWorkLocations()));
    navigate("/job-portal/home");
  }

  return (
    <Container
      fluid
      className="d-flex flex-column px-5 pt-5 bg-light home-container bg-all"
    >
      <Row>
        <Col>
          <SearchBar jobId={jobId} />
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <div
            className="candidate-portfolio-banner"
            onClick={() => {
              navigate("/candidate/login");
            }}
          >
            <span>Build Your Free Online CV Portfolio</span>
            <span className="mdi mdi-arrow-right"></span>
          </div>
        </Col>
      </Row>
      <Row className="m-2">
        <Col>
          <h5>Jobs ({jobData.totalElements || 0})</h5>
        </Col>
      </Row>
      <Row className="mx-2">
        <Col>
          <FilterBubble />
        </Col>
      </Row>
      {jobId && (
        <Row className="mx-2">
          <Col>
            <Button onClick={returnToJob} className="back-btn">
              <span className="mdi mdi-chevron-left me-2"></span>Back to All
              Jobs
            </Button>
          </Col>
        </Row>
      )}

      {jobListRaw.length > 0 ? (
        <Row className="mx-2 mt-2 mb-4">
          {windowWidth >= 1000 && (
            <>
              <Col
                id="scrollableDiv"
                className="overflow-y-scroll vh-100"
                xs="4"
                xxl="3"
              >
                <JobList
                  jobListRaw={jobListRaw}
                  selectedJob={selectedJob}
                  pageNumber={pageNumber}
                  isLastPage={jobData.last}
                  handleJobSelect={handleJobSelect}
                />
              </Col>
              <Col className="overflow-y-scroll vh-100 no-bar" xs="8" xxl="9">
                {selectedJob ? (
                  <JobDetailsCard selectedJob={selectedJob} />
                ) : (
                  "Unable to load job."
                )}
              </Col>
            </>
          )}
          {windowWidth < 1000 && (
            <>
              <Col xs="12" sm="auto">
                <JobList
                  jobListRaw={jobListRaw}
                  selectedJob={selectedJob}
                  pageNumber={pageNumber}
                  isLastPage={jobData.last}
                  handleJobSelect={handleJobSelect}
                />
              </Col>
              <Col>
                {selectedJob && (
                  <JobDetailsOffcanvas
                    selectedJob={selectedJob}
                    canvasOpen={canvasOpen}
                    handleCanvasClose={handleCanvasClose}
                  />
                )}
              </Col>
            </>
          )}
        </Row>
      ) : (
        <Row className="flex-column mt-5 text-center">
          <h2>Oops...No jobs found.</h2>
          <h6>Try to adjust your filters or use a different search term.</h6>
        </Row>
      )}
    </Container>
  );
}
